import { useEffect, useState } from "react";
import api, { formatDateTime } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bell, MessageCircle, Mail, MessageSquare } from "lucide-react";

const CHANNEL_ICONS = {
  whatsapp: MessageCircle,
  sms: MessageSquare,
  email: Mail,
};

const TIPO_LABEL = {
  en_camino: "En camino",
  entregado: "Entregado",
  fallido: "Fallido",
  pagado: "Pago recibido",
};

export default function Notificaciones() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => {
      setItems(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="notificaciones-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Comunicaciones</div>
        <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
          <Bell className="w-8 h-8" /> Notificaciones
        </h1>
        <p className="text-zinc-500 mt-2 text-sm">
          Vista previa de mensajes al cliente. En producción se enviarán por WhatsApp/SMS cuando conectes el proveedor.
        </p>
      </header>

      <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-xs text-amber-900 flex items-start gap-2">
        <span className="font-semibold uppercase tracking-widest">MOCKED</span>
        <span>Modo preview: los mensajes se guardan en el sistema pero no se envían. Configura Twilio en el backend para enviarlos.</span>
      </div>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Canal</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Evento</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Pedido</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Cliente</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Mensaje</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {loading && (
                <tr><td colSpan={6} className="text-center text-zinc-400 py-8">Cargando...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="text-center text-zinc-400 py-10">
                  Aún no hay notificaciones. Se generan automáticamente al cambiar el estado de entrega o al facturar.
                </td></tr>
              )}
              {items.map((n) => {
                const Icon = CHANNEL_ICONS[n.channel] || Bell;
                return (
                  <tr key={n.id} className="hover:bg-zinc-50">
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest">
                        <Icon className="w-3.5 h-3.5" /> {n.channel}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <Badge className="bg-zinc-900 text-white hover:bg-zinc-900 rounded-full text-[10px] uppercase tracking-widest">
                        {TIPO_LABEL[n.tipo] || n.tipo}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs">{n.order_numero}</td>
                    <td className="px-5 py-3">
                      <div className="font-semibold">{n.cliente_nombre}</div>
                      {n.cliente_telefono && <div className="text-xs text-zinc-500">{n.cliente_telefono}</div>}
                    </td>
                    <td className="px-5 py-3 text-zinc-600 max-w-md">{n.mensaje}</td>
                    <td className="px-5 py-3 text-zinc-500 text-xs">{formatDateTime(n.at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
